import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import {
  CheckIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ApiError } from "@/lib/apiClient";
import {
  addOrderLinkedAssets,
  getAuftragHeroIds,
  getOrderLinkedAssets,
  removeOrderLinkedAsset,
  type AuftragLinkedAsset,
} from "@/lib/projectOverview";
import type { Order } from "@/lib/projects";
import {
  getAddressAssets,
  getPublishedFormConfig,
  type AddressAsset,
} from "@/lib/wartungen";
import { normalizeFields } from "@/pages/wartungen/form/runtimeModel";
import { buildMasterdataDisplayRows } from "@/pages/wartungen/huelleDetail/masterdataDisplay";
import { OverviewCard } from "@/projectOverview/OverviewCard";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { PROJECT_OVERVIEW_OVERLAY_Z } from "@/projectOverview/projectOverviewLayers";

function assetTitle(asset: AuftragLinkedAsset): string {
  const kennung = String(asset.external_id || "").trim();
  const name = String(asset.name || "").trim();
  if (kennung && name && name !== kennung) return `${kennung} · ${name}`;
  return kennung || name || `Objekt #${asset.asset_id}`;
}

function candidateParts(asset: AddressAsset): {
  id: number;
  kennung: string;
  name: string;
  typeName: string;
  subLocation: string;
} {
  const id = Number(asset.asset_id ?? asset.id);
  const kennung = String(asset.external_id || "").trim();
  const name = String(asset.name || "").trim();
  const typeName = String(asset.maintenance_type_name || asset.maintenance_type_key || "").trim();
  const subLocation = String(asset.sub_location_name || "").trim();
  return {
    id,
    kennung: kennung || `Objekt #${Number.isFinite(id) ? id : "?"}`,
    name: name && name !== kennung ? name : "",
    typeName,
    subLocation,
  };
}

function candidateChipLabel(asset: AddressAsset): string {
  const { kennung, name } = candidateParts(asset);
  return name ? `${kennung} · ${name}` : kennung;
}

export function LinkedAssetsSection({
  project,
  canEdit = true,
}: {
  project: Order;
  canEdit?: boolean;
}) {
  const [assets, setAssets] = useState<AuftragLinkedAsset[]>([]);
  const [heroAddressId, setHeroAddressId] = useState<number | null>(
    project.hero_address_id != null ? Number(project.hero_address_id) : null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyAssetId, setBusyAssetId] = useState<number | null>(null);

  const [detailAsset, setDetailAsset] = useState<AuftragLinkedAsset | null>(null);
  const [detailRows, setDetailRows] = useState<Array<{ key: string; label: string; value: string }>>(
    [],
  );
  const [detailLoading, setDetailLoading] = useState(false);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [candidates, setCandidates] = useState<AddressAsset[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidateQuery, setCandidateQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [savingLinks, setSavingLinks] = useState(false);

  const loadedKeyRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    const key = `${project.id}:${project.hero_address_id ?? ""}`;
    const silent = loadedKeyRef.current === key;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await getOrderLinkedAssets(project.id);
      setAssets(Array.isArray(data.assets) ? data.assets : []);
      let addressId =
        data.hero_address_id != null
          ? Number(data.hero_address_id)
          : project.hero_address_id != null
            ? Number(project.hero_address_id)
            : null;
      if (!addressId || addressId <= 0) {
        const heroIds = await getAuftragHeroIds(project.id).catch(() => null);
        if (heroIds?.hero_address_id) addressId = Number(heroIds.hero_address_id);
      }
      setHeroAddressId(addressId && addressId > 0 ? addressId : null);
      loadedKeyRef.current = key;
    } catch (err) {
      if (!silent) setAssets([]);
      setError(err instanceof ApiError ? err.message : "Verknüpfte Objekte konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [project.id, project.hero_address_id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!detailAsset) {
      setDetailRows([]);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    void (async () => {
      try {
        const masterdata =
          detailAsset.masterdata && typeof detailAsset.masterdata === "object"
            ? detailAsset.masterdata
            : {};
        const typeKey = String(detailAsset.maintenance_type_key || "").trim();
        let fields = [] as ReturnType<typeof normalizeFields>;
        if (typeKey) {
          const schema = await getPublishedFormConfig({
            typeKey,
            heroAddressId: detailAsset.hero_address_id ?? heroAddressId,
          }).catch(() => null);
          if (schema) {
            fields = normalizeFields(schema).filter((f) => f.effective_scope === "masterdata");
          }
        }
        const rows = buildMasterdataDisplayRows(masterdata, fields);
        if (!cancelled) setDetailRows(rows);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detailAsset, heroAddressId]);

  const linkedIds = useMemo(() => new Set(assets.map((a) => a.asset_id)), [assets]);

  const availableCandidates = useMemo(
    () =>
      candidates.filter((c) => {
        const id = Number(c.asset_id ?? c.id);
        return Number.isFinite(id) && id > 0 && !linkedIds.has(id);
      }),
    [candidates, linkedIds],
  );

  const filteredCandidates = useMemo(() => {
    const q = candidateQuery.trim().toLowerCase();
    if (!q) return availableCandidates;
    return availableCandidates.filter((c) => {
      const hay = [
        c.external_id,
        c.name,
        c.maintenance_type_name,
        c.maintenance_type_key,
        c.sub_location_name,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [availableCandidates, candidateQuery]);

  const groupedCandidates = useMemo(() => {
    const map = new Map<string, { label: string; items: AddressAsset[] }>();
    for (const asset of filteredCandidates) {
      const key = String(asset.maintenance_type_key || "sonstige");
      const label = String(asset.maintenance_type_name || key || "Sonstige");
      if (!map.has(key)) map.set(key, { label, items: [] });
      map.get(key)!.items.push(asset);
    }
    return Array.from(map.entries())
      .map(([key, group]) => ({ key, ...group }))
      .sort((a, b) => a.label.localeCompare(b.label, "de"));
  }, [filteredCandidates]);

  const selectedAssets = useMemo(
    () => availableCandidates.filter((c) => selectedIds.has(Number(c.asset_id ?? c.id))),
    [availableCandidates, selectedIds],
  );

  const allFilteredSelected =
    filteredCandidates.length > 0 &&
    filteredCandidates.every((c) => selectedIds.has(Number(c.asset_id ?? c.id)));

  function toggleId(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllFiltered() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        for (const c of filteredCandidates) next.delete(Number(c.asset_id ?? c.id));
      } else {
        for (const c of filteredCandidates) next.add(Number(c.asset_id ?? c.id));
      }
      return next;
    });
  }

  async function openPicker() {
    if (!heroAddressId) {
      setError("Dem Projekt ist kein Ort mit Hero-Adresse zugeordnet.");
      return;
    }
    setPickerOpen(true);
    setCandidateQuery("");
    setSelectedIds(new Set());
    setCandidatesLoading(true);
    try {
      const list = await getAddressAssets(heroAddressId);
      setCandidates(list);
    } catch (err) {
      setCandidates([]);
      setError(err instanceof ApiError ? err.message : "Objekte am Ort konnten nicht geladen werden.");
    } finally {
      setCandidatesLoading(false);
    }
  }

  async function saveSelectedLinks() {
    const ids = Array.from(selectedIds);
    if (!ids.length) {
      setPickerOpen(false);
      return;
    }
    setSavingLinks(true);
    setError(null);
    try {
      const data = await addOrderLinkedAssets(project.id, ids);
      setAssets(Array.isArray(data.assets) ? data.assets : []);
      setPickerOpen(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Verknüpfung fehlgeschlagen.");
    } finally {
      setSavingLinks(false);
    }
  }

  async function unlink(assetId: number) {
    setBusyAssetId(assetId);
    setError(null);
    try {
      const data = await removeOrderLinkedAsset(project.id, assetId);
      setAssets(Array.isArray(data.assets) ? data.assets : []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Entfernen fehlgeschlagen.");
    } finally {
      setBusyAssetId(null);
    }
  }

  const ortLabel = String(project.ort || "").trim();

  return (
    <>
      <OverviewCard title="Verknüpfte Türen / Wartungsobjekte">
        {loading && assets.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-label-tertiary">
            <Spinner /> Wird geladen …
          </div>
        ) : null}

        {!loading && !assets.length ? (
          <p className="text-sm text-label-tertiary">Keine Wartungsobjekte verknüpft.</p>
        ) : null}

        {assets.length > 0 ? (
          <ul className="space-y-2">
            {assets.map((asset) => (
              <li
                key={asset.asset_id}
                className="flex items-center justify-between gap-3 rounded-ios border border-separator bg-fill-quaternary/40 px-3 py-2.5"
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => setDetailAsset(asset)}
                  title="Stammdaten anzeigen"
                >
                  <div className="truncate text-sm font-medium text-label">{assetTitle(asset)}</div>
                  <div className="truncate text-xs text-label-tertiary">
                    {asset.maintenance_type_name || asset.maintenance_type_key || "Wartungsobjekt"}
                  </div>
                </button>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button
                    type="button"
                    className="rounded-full border border-separator bg-card px-2.5 py-1 text-xs font-medium text-label hover:bg-fill-tertiary"
                    onClick={() => setDetailAsset(asset)}
                  >
                    Stammdaten
                  </button>
                  {canEdit ? (
                    <button
                      type="button"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-separator bg-card text-label-secondary hover:bg-status-red/10 hover:text-status-red disabled:opacity-50"
                      disabled={busyAssetId === asset.asset_id}
                      onClick={() => void unlink(asset.asset_id)}
                      title="Verknüpfung entfernen"
                      aria-label="Verknüpfung entfernen"
                    >
                      {busyAssetId === asset.asset_id ? (
                        <Spinner className="h-3.5 w-3.5" />
                      ) : (
                        <XMarkIcon className="h-3.5 w-3.5" aria-hidden />
                      )}
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : null}

        {canEdit ? (
          <div className="mt-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!heroAddressId}
              onClick={() => void openPicker()}
              title={
                heroAddressId
                  ? "Weitere Wartungsobjekte am Projekt-Ort verknüpfen"
                  : "Kein Ort mit Hero-Adresse am Projekt"
              }
            >
              Türen verknüpfen
            </Button>
            {!heroAddressId ? (
              <p className="mt-1 text-xs text-label-tertiary">
                Verknüpfung erst möglich, wenn dem Projekt ein Ort zugeordnet ist.
              </p>
            ) : null}
          </div>
        ) : null}

        {error ? <p className="mt-2 text-xs text-status-red">{error}</p> : null}
      </OverviewCard>

      <Dialog
        open={!!detailAsset}
        onClose={() => setDetailAsset(null)}
        title={detailAsset ? `Stammdaten · ${assetTitle(detailAsset)}` : "Stammdaten"}
        size="lg"
        plainBackdrop
        zIndexClass={PROJECT_OVERVIEW_OVERLAY_Z}
        footer={
          <DialogCancelButton onClose={() => setDetailAsset(null)} label="Schließen" />
        }
      >
        {detailAsset ? (
          <div className="space-y-3">
            <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
              <div className="flex justify-between gap-4 border-b border-separator/50 py-1">
                <dt className="text-sm text-label-secondary">Kennung</dt>
                <dd className="text-sm text-label">{detailAsset.external_id || "—"}</dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-separator/50 py-1">
                <dt className="text-sm text-label-secondary">Kategorie</dt>
                <dd className="text-sm text-label">
                  {detailAsset.maintenance_type_name || detailAsset.maintenance_type_key || "—"}
                </dd>
              </div>
            </dl>
            {detailLoading ? (
              <div className="flex items-center gap-2 text-sm text-label-tertiary">
                <Spinner /> Stammdaten werden geladen …
              </div>
            ) : detailRows.length ? (
              <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
                {detailRows.map((row) => (
                  <div
                    key={row.key}
                    className="flex justify-between gap-4 border-b border-separator/50 py-1"
                  >
                    <dt className="text-sm text-label-secondary">{row.label}</dt>
                    <dd className="text-right text-sm text-label">{row.value}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-sm text-label-tertiary">Keine Stammdaten hinterlegt.</p>
            )}
          </div>
        ) : null}
      </Dialog>

      <Dialog
        open={pickerOpen}
        onClose={() => {
          if (!savingLinks) setPickerOpen(false);
        }}
        title="Türen verknüpfen"
        description={
          ortLabel
            ? `Aktive Wartungsobjekte am Ort „${ortLabel}“. Bereits verknüpfte Objekte sind ausgeblendet.`
            : "Aktive Wartungsobjekte am Projekt-Ort. Bereits verknüpfte Objekte sind ausgeblendet."
        }
        size="xl"
        fullScreenMobile
        plainBackdrop
        zIndexClass={PROJECT_OVERVIEW_OVERLAY_Z}
        bodyClassName="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden"
        footer={
          <>
            <DialogCancelButton
              disabled={savingLinks}
              onClose={() => setPickerOpen(false)}
              label="Abbrechen"
            />
            <Button
              type="button"
              disabled={savingLinks || selectedIds.size === 0}
              onClick={() => void saveSelectedLinks()}
            >
              {savingLinks
                ? "Speichern …"
                : selectedIds.size === 0
                  ? "Verknüpfen"
                  : selectedIds.size === 1
                    ? "1 Tür verknüpfen"
                    : `${selectedIds.size} Türen verknüpfen`}
            </Button>
          </>
        }
      >
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <div className="relative shrink-0">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-label-tertiary"
              aria-hidden
            />
            <input
              type="search"
              className="eh-input w-full pl-9"
              placeholder="Kennung, Name oder Kategorie suchen …"
              value={candidateQuery}
              onChange={(e) => setCandidateQuery(e.target.value)}
              autoFocus
            />
          </div>

          {selectedAssets.length > 0 ? (
            <div className="shrink-0 rounded-ios border border-accent/25 bg-accent-soft/40 px-3 py-2.5">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-accent">
                  Auswahl ({selectedAssets.length})
                </p>
                <button
                  type="button"
                  className="text-xs font-medium text-accent hover:underline"
                  onClick={() => setSelectedIds(new Set())}
                >
                  Auswahl leeren
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {selectedAssets.map((asset) => {
                  const id = Number(asset.asset_id ?? asset.id);
                  return (
                    <button
                      key={id}
                      type="button"
                      className="inline-flex max-w-full items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-xs font-medium text-label-on-accent"
                      onClick={() => toggleId(id)}
                      title="Auswahl entfernen"
                    >
                      <span className="truncate">{candidateChipLabel(asset)}</span>
                      <XMarkIcon className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-ios border border-separator bg-fill-quaternary/30">
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-separator px-3 py-2">
              <p className="text-xs text-label-secondary">
                {candidatesLoading
                  ? "Objekte werden geladen …"
                  : filteredCandidates.length === 0
                    ? "Keine Treffer"
                    : `${filteredCandidates.length} Objekt${filteredCandidates.length === 1 ? "" : "e"}`}
              </p>
              {!candidatesLoading && filteredCandidates.length > 0 ? (
                <button
                  type="button"
                  className="text-xs font-medium text-accent hover:underline"
                  onClick={toggleAllFiltered}
                >
                  {allFilteredSelected ? "Alle abwählen" : "Alle auswählen"}
                </button>
              ) : null}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {candidatesLoading ? (
                <div className="flex items-center justify-center gap-2 py-12 text-sm text-label-tertiary">
                  <Spinner /> Objekte werden geladen …
                </div>
              ) : filteredCandidates.length === 0 ? (
                <div className="px-3 py-12 text-center">
                  <p className="text-sm font-medium text-label">
                    {availableCandidates.length === 0
                      ? "Keine weiteren Objekte am Ort"
                      : "Keine Treffer"}
                  </p>
                  <p className="mt-1 text-xs text-label-tertiary">
                    {availableCandidates.length === 0
                      ? "Alle vorhandenen Objekte sind bereits verknüpft oder es gibt noch keine am Ort."
                      : "Suche anpassen oder zurücksetzen."}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {groupedCandidates.map((group) => (
                    <section key={group.key}>
                      <h4 className="sticky top-0 z-[1] bg-fill-quaternary/95 px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-label-tertiary backdrop-blur-sm">
                        {group.label}
                        <span className="ml-1.5 font-normal normal-case tracking-normal text-label-tertiary/80">
                          {group.items.length}
                        </span>
                      </h4>
                      <ul className="mt-1 space-y-1">
                        {group.items.map((asset) => {
                          const parts = candidateParts(asset);
                          const checked = selectedIds.has(parts.id);
                          return (
                            <li key={parts.id}>
                              <button
                                type="button"
                                onClick={() => toggleId(parts.id)}
                                className={clsx(
                                  "flex w-full items-center gap-3 rounded-ios px-3 py-2.5 text-left transition",
                                  checked
                                    ? "bg-accent-soft ring-1 ring-accent/35"
                                    : "bg-card hover:bg-fill-tertiary",
                                )}
                              >
                                <span
                                  className={clsx(
                                    "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition",
                                    checked
                                      ? "border-accent bg-accent text-label-on-accent"
                                      : "border-separator bg-card text-transparent",
                                  )}
                                  aria-hidden
                                >
                                  <CheckIcon className="h-3.5 w-3.5" strokeWidth={2.5} />
                                </span>
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate text-sm font-semibold text-label">
                                    {parts.kennung}
                                  </span>
                                  <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-label-tertiary">
                                    {parts.name ? <span className="truncate">{parts.name}</span> : null}
                                    {parts.subLocation ? (
                                      <span className="truncate">{parts.subLocation}</span>
                                    ) : null}
                                  </span>
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}
